import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, FunnelChart, Funnel, LabelList,
  AreaChart, Area, Legend
} from "recharts";
import {
  Briefcase,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  DollarSign,
  Target,
  CheckCircle,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  FileSpreadsheet,
  Download,
  Filter,
  Award,
  Zap,
  ChevronDown,
  UserCheck,
  XCircle,
  MessageSquare,
  Share2
} from "lucide-react";

const COLORS = ["#dc2626", "#ef4444", "#f87171", "#fca5a5", "#fecaca"];
const SOURCE_COLORS = ["#dc2626", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"];
const FUNNEL_COLORS = ["#dc2626", "#ef4444", "#f97316", "#eab308", "#22c55e"];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1a1a1a] border border-[#333] px-3 py-2 text-sm">
        <p className="text-white font-medium">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color || "#dc2626" }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const StatCard = ({ icon: Icon, label, value, trend, trendLabel, delay = 0 }) => (
  <motion.div
    className="bg-[#111] border border-[#222] p-5 hover:border-[#333] transition-colors"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 bg-crimson/15 flex items-center justify-center">
        <Icon className="w-4 h-4 text-crimson" />
      </div>
      {trend !== undefined && trend !== null && (
        <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="font-heading text-2xl font-bold text-white">{value}</div>
    <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    {trendLabel && <div className="text-[10px] text-gray-600 mt-1">{trendLabel}</div>}
  </motion.div>
);

const RecruiterAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30d");
  const [exporting, setExporting] = useState(null);
  const [shareData, setShareData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchShareAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/analytics/recruiter/advanced?time_range_str=${timeRange}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        toast.error("Failed to fetch analytics");
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchShareAnalytics = async () => {
    try {
      const res = await fetch(`${API}/analytics/shares?range=${timeRange}`, { credentials: "include" });
      if (res.ok) {
        setShareData(await res.json());
      }
    } catch (e) {
      console.error("Failed to fetch share analytics:", e);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await fetch(`${API}/reports/analytics/${format}`, {
        credentials: "include",
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `recruiter_analytics_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`Exported as ${format.toUpperCase()}`);
      } else {
        toast.error("Export failed");
      }
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  // Prepare funnel data
  const funnelData = analytics?.hiring_funnel
    ? [
        { name: "Applied", value: analytics.hiring_funnel.applied, fill: FUNNEL_COLORS[0] },
        { name: "Screened", value: analytics.hiring_funnel.screened, fill: FUNNEL_COLORS[1] },
        { name: "Interviewed", value: analytics.hiring_funnel.interviewed, fill: FUNNEL_COLORS[2] },
        { name: "Offered", value: analytics.hiring_funnel.offered, fill: FUNNEL_COLORS[3] },
        { name: "Hired", value: analytics.hiring_funnel.hired, fill: FUNNEL_COLORS[4] },
      ]
    : [];

  // Source effectiveness chart data
  const sourceData = analytics?.source_effectiveness || [];

  // Offer acceptance data for pie chart
  const offerPieData = analytics?.offer_acceptance
    ? [
        { name: "Accepted", value: analytics.offer_acceptance.accepted || 0, fill: "#22c55e" },
        { name: "Pending", value: analytics.offer_acceptance.pending || 0, fill: "#eab308" },
        { name: "Declined", value: analytics.offer_acceptance.declined || 0, fill: "#ef4444" },
      ].filter(d => d.value > 0)
    : [];

  // Time to hire trend data
  const tthTrend = (analytics?.time_to_hire_trend || []).filter(t => t.average_days !== null);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Sidebar />
        <main className="lg:ml-64 p-6 lg:p-8 pt-20 lg:pt-8">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  const ov = analytics?.overview || {};
  const tth = analytics?.time_to_hire || {};
  const cph = analytics?.cost_per_hire || {};
  const oa = analytics?.offer_acceptance || {};

  return (
    <div className="min-h-screen bg-obsidian" data-testid="recruiter-analytics">
      <Sidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">
              HIRING <span className="text-crimson">ANALYTICS</span>
            </h1>
            <p className="text-gray-400 text-sm mt-1">Hiring funnel metrics & performance insights</p>
          </motion.div>
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="bg-[#111] border border-[#333] text-white text-sm px-3 py-2 focus:border-crimson outline-none"
              data-testid="time-range-select"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <Button
              onClick={() => handleExport("csv")}
              variant="outline"
              size="sm"
              className="border-[#333] hover:border-crimson text-white"
              disabled={!!exporting}
              data-testid="export-csv-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button
              onClick={() => handleExport("pdf")}
              variant="outline"
              size="sm"
              className="border-[#333] hover:border-crimson text-white"
              disabled={!!exporting}
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <StatCard icon={Briefcase} label="Active Jobs" value={ov.active_jobs || 0} trend={null} delay={0} />
          <StatCard icon={Users} label="Applicants" value={ov.total_applicants || 0} trend={analytics?.trends?.applicants_change} delay={0.05} />
          <StatCard icon={MessageSquare} label="Interviews" value={ov.interviews_scheduled || 0} delay={0.1} />
          <StatCard icon={Target} label="Offers Sent" value={ov.offers_sent || 0} delay={0.15} />
          <StatCard icon={UserCheck} label="Hires" value={ov.hires || 0} delay={0.2} />
          <StatCard icon={Briefcase} label="Total Jobs" value={ov.total_jobs || 0} delay={0.25} />
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Time to Hire */}
          <motion.div
            className="bg-[#111] border border-[#222] p-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="time-to-hire-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-crimson" />
              <span className="text-xs uppercase tracking-wider text-gray-400">Avg Time to Hire</span>
            </div>
            <div className="font-heading text-3xl font-bold text-white">{tth.average_days || 0}<span className="text-lg text-gray-400 ml-1">days</span></div>
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              <span>Fastest: <span className="text-emerald-400">{tth.fastest || 0}d</span></span>
              <span>Slowest: <span className="text-red-400">{tth.slowest || 0}d</span></span>
            </div>
          </motion.div>

          {/* Cost per Hire */}
          <motion.div
            className="bg-[#111] border border-[#222] p-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            data-testid="cost-per-hire-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-crimson" />
              <span className="text-xs uppercase tracking-wider text-gray-400">Cost per Hire</span>
            </div>
            <div className="font-heading text-3xl font-bold text-white">${cph.total || 0}</div>
            <div className="flex flex-col gap-1 mt-3 text-xs text-gray-500">
              <span>Platform: ${cph.platform_cost || 0}/mo</span>
              <span>Total spend: ${cph.total_recruitment_cost || 0}</span>
            </div>
          </motion.div>

          {/* Offer Acceptance Rate */}
          <motion.div
            className="bg-[#111] border border-[#222] p-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            data-testid="offer-acceptance-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-4 h-4 text-crimson" />
              <span className="text-xs uppercase tracking-wider text-gray-400">Offer Acceptance</span>
            </div>
            <div className="font-heading text-3xl font-bold text-white">{oa.rate || 0}<span className="text-lg text-gray-400 ml-1">%</span></div>
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              <span className="text-emerald-400">{oa.accepted || 0} accepted</span>
              <span className="text-yellow-400">{oa.pending || 0} pending</span>
            </div>
          </motion.div>

          {/* Conversion Rate */}
          <motion.div
            className="bg-[#111] border border-[#222] p-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            data-testid="conversion-rate-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-crimson" />
              <span className="text-xs uppercase tracking-wider text-gray-400">Conversion Rate</span>
            </div>
            <div className="font-heading text-3xl font-bold text-white">
              {analytics?.trends?.conversion_rate || 0}
              <span className="text-lg text-gray-400 ml-1">apps/job</span>
            </div>
            <div className="flex items-center gap-1 mt-3 text-xs text-gray-500">
              <span>{ov.total_applicants || 0} applicants across {ov.total_jobs || 0} jobs</span>
            </div>
          </motion.div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Hiring Funnel */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            data-testid="hiring-funnel-chart"
          >
            <h3 className="font-heading text-lg font-bold mb-1">HIRING FUNNEL</h3>
            <p className="text-xs text-gray-500 mb-4">Candidate progression through hiring stages</p>
            {funnelData.length > 0 && funnelData.some(d => d.value > 0) ? (
              <div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={funnelData} layout="vertical" margin={{ left: 20, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" horizontal={false} />
                    <XAxis type="number" stroke="#555" tick={{ fill: '#888', fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" stroke="#555" tick={{ fill: '#ccc', fontSize: 12 }} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="Candidates" radius={[0, 4, 4, 0]}>
                      {funnelData.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Conversion rates between stages */}
                <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
                  {funnelData.slice(0, -1).map((stage, i) => {
                    const next = funnelData[i + 1];
                    const rate = stage.value > 0 ? Math.round((next.value / stage.value) * 100) : 0;
                    return (
                      <div key={stage.name} className="flex items-center gap-1 text-[10px] text-gray-500">
                        <span>{stage.name}</span>
                        <span className="text-crimson font-bold">{rate}%</span>
                        <span className="text-gray-600">&rarr;</span>
                      </div>
                    );
                  })}
                  <span className="text-[10px] text-emerald-400">Hired</span>
                </div>
              </div>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-gray-500 text-sm">
                No hiring funnel data yet. Post jobs and receive applications to see metrics.
              </div>
            )}
          </motion.div>

          {/* Source Effectiveness */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            data-testid="source-effectiveness-chart"
          >
            <h3 className="font-heading text-lg font-bold mb-1">SOURCE EFFECTIVENESS</h3>
            <p className="text-xs text-gray-500 mb-4">Applicant quality by recruitment source</p>
            {sourceData.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={sourceData} margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                    <XAxis dataKey="source" stroke="#555" tick={{ fill: '#ccc', fontSize: 12 }} />
                    <YAxis stroke="#555" tick={{ fill: '#888', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#888' }} />
                    <Bar dataKey="applied" name="Applied" fill="#dc2626" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="screened" name="Screened" fill="#f97316" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="hired" name="Hired" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {/* Conversion rates per source */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {sourceData.map((s, i) => (
                    <div key={s.source} className="flex items-center justify-between text-xs px-2 py-1 bg-[#0a0a0a] border border-[#1a1a1a]">
                      <span className="text-gray-400">{s.source}</span>
                      <span className="text-crimson font-medium">{s.conversion_rate}% hire rate</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-gray-500 text-sm">
                No source data available yet.
              </div>
            )}
          </motion.div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Offer Acceptance Breakdown */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            data-testid="offer-acceptance-chart"
          >
            <h3 className="font-heading text-lg font-bold mb-1">OFFER STATUS</h3>
            <p className="text-xs text-gray-500 mb-4">Breakdown of offer outcomes</p>
            {offerPieData.length > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={offerPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {offerPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4 mt-2">
                  {offerPieData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
                      <span className="text-gray-400">{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-500 text-sm">
                No offers sent yet.
              </div>
            )}
          </motion.div>

          {/* Time to Hire Trend */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6 lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            data-testid="time-to-hire-trend-chart"
          >
            <h3 className="font-heading text-lg font-bold mb-1">TIME TO HIRE TREND</h3>
            <p className="text-xs text-gray-500 mb-4">Average days to hire over time</p>
            {tthTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={tthTrend} margin={{ left: 0, right: 10 }}>
                  <defs>
                    <linearGradient id="tthGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                  <XAxis dataKey="month" stroke="#555" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis stroke="#555" tick={{ fill: '#888', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="average_days"
                    name="Avg Days"
                    stroke="#dc2626"
                    fill="url(#tthGradient)"
                    strokeWidth={2}
                  />
                  <Bar dataKey="hires" name="Hires" fill="#22c55e" opacity={0.5} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-500 text-sm">
                Hire more candidates to see time-to-hire trends.
              </div>
            )}
          </motion.div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Performing Jobs */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            data-testid="top-jobs-section"
          >
            <h3 className="font-heading text-lg font-bold mb-1">TOP PERFORMING JOBS</h3>
            <p className="text-xs text-gray-500 mb-4">Highest applicant volume & conversion</p>
            <div className="space-y-3">
              {(analytics?.top_performing_jobs || []).map((job, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-[#0a0a0a] border border-[#1a1a1a] hover:border-crimson/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-crimson/20 flex items-center justify-center text-crimson font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium">{job.title}</h4>
                      <p className="text-[10px] text-gray-500">{job.views} views</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-crimson">{job.applicants}</div>
                    <div className="text-[10px] text-gray-500">{job.conversion}% conv.</div>
                  </div>
                </div>
              ))}
              {(!analytics?.top_performing_jobs || analytics.top_performing_jobs.length === 0) && (
                <p className="text-gray-500 text-center py-6 text-sm">No jobs posted yet</p>
              )}
            </div>
          </motion.div>

          {/* Top Skills in Demand */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75 }}
            data-testid="skills-demand-section"
          >
            <h3 className="font-heading text-lg font-bold mb-1">SKILLS IN DEMAND</h3>
            <p className="text-xs text-gray-500 mb-4">Most requested skills across your job postings</p>
            <div className="space-y-3">
              {(analytics?.top_skills_demand || []).map((skill, index) => {
                const maxCount = Math.max(...(analytics?.top_skills_demand || []).map(s => s.count), 1);
                const pct = (skill.count / maxCount) * 100;
                return (
                  <div key={skill.skill}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{skill.skill}</span>
                      <span className="text-xs text-gray-400">{skill.count} jobs</span>
                    </div>
                    <div className="h-2 bg-[#1a1a1a] overflow-hidden">
                      <motion.div
                        className="h-full bg-crimson"
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!analytics?.top_skills_demand || analytics.top_skills_demand.length === 0) && (
                <p className="text-gray-500 text-center py-6 text-sm">Add skills to your job postings to see demand</p>
              )}
            </div>
          </motion.div>
        </div>

        {/* Applicant Sources Pie */}
        <motion.div
          className="bg-[#111] border border-[#222] p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          data-testid="applicant-sources-section"
        >
          <h3 className="font-heading text-lg font-bold mb-1">APPLICANT SOURCES</h3>
          <p className="text-xs text-gray-500 mb-4">Where your applicants are coming from</p>
          <div className="flex flex-col lg:flex-row items-center gap-8">
            {(analytics?.applicant_sources || []).length > 0 ? (
              <>
                <ResponsiveContainer width={240} height={200}>
                  <PieChart>
                    <Pie
                      data={analytics.applicant_sources}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="count"
                      nameKey="source"
                    >
                      {(analytics.applicant_sources || []).map((entry, i) => (
                        <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {(analytics.applicant_sources || []).map((s, i) => (
                    <div key={s.source} className="flex items-center justify-between px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a]">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />
                        <span className="text-sm">{s.source}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-gray-400">{s.count} applicants</span>
                        <span className="text-crimson font-medium">{s.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="w-full text-center py-8 text-gray-500 text-sm">No applicant source data available</div>
            )}
          </div>
        </motion.div>

        {/* Share Analytics Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-crimson" />
            <h2 className="font-heading text-xl font-bold">SOCIAL SHARE <span className="text-crimson">ANALYTICS</span></h2>
          </div>

          {shareData && shareData.total_shares > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Platform Breakdown */}
              <div className="bg-[#111] border border-[#222] p-6" data-testid="share-platform-chart">
                <h3 className="text-sm text-gray-400 mb-4 uppercase tracking-wide">Shares by Platform</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={shareData.by_platform.filter(p => p.count > 0)} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                    <XAxis type="number" tick={{ fill: "#888", fontSize: 12 }} />
                    <YAxis dataKey="platform" type="category" tick={{ fill: "#ccc", fontSize: 12 }} width={80}
                      tickFormatter={(v) => ({ twitter: "Twitter/X", linkedin: "LinkedIn", facebook: "Facebook", whatsapp: "WhatsApp", email: "Email", copy_link: "Copy Link" }[v] || v)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Shares" radius={[0, 4, 4, 0]}>
                      {shareData.by_platform.filter(p => p.count > 0).map((_, i) => (
                        <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 text-center">
                  <span className="text-2xl font-bold text-white">{shareData.total_shares}</span>
                  <span className="text-gray-400 text-sm ml-2">total shares</span>
                </div>
              </div>

              {/* Top Shared Jobs */}
              <div className="bg-[#111] border border-[#222] p-6" data-testid="share-top-jobs">
                <h3 className="text-sm text-gray-400 mb-4 uppercase tracking-wide">Top Shared Jobs</h3>
                {shareData.top_jobs?.filter(j => j.shares > 0).length > 0 ? (
                  <div className="space-y-3">
                    {shareData.top_jobs.filter(j => j.shares > 0).slice(0, 5).map((job, i) => (
                      <div key={job.job_id} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-crimson font-bold text-sm w-5">{i + 1}</span>
                          <span className="text-white text-sm truncate">{job.title}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Share2 className="w-3 h-3 text-gray-500" />
                          <span className="text-crimson font-medium text-sm">{job.shares}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 text-sm">No top shared jobs yet</div>
                )}
              </div>

              {/* Daily Trend */}
              {shareData.daily_trend?.length > 1 && (
                <div className="bg-[#111] border border-[#222] p-6 lg:col-span-2" data-testid="share-daily-trend">
                  <h3 className="text-sm text-gray-400 mb-4 uppercase tracking-wide">Share Activity Trend</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={shareData.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                      <XAxis dataKey="date" tick={{ fill: "#888", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fill: "#888", fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="shares" name="Shares" stroke="#dc2626" fill="#dc262640" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-[#111] border border-[#222] p-8 text-center" data-testid="share-empty-state">
              <Share2 className="w-8 h-8 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No share data yet. When your jobs get shared on social media, analytics will appear here.</p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default RecruiterAnalytics;
